import supabase from '../utils/supabaseUtils';
import { addWatermark } from '../utils/imageUtils';

// Service to handle removeBG API integration
const REMOVE_BG_API_URL = 'https://api.remove.bg/v1.0/removebg';

// For development, directly use the Express server
// In production, this should be adjusted based on the environment
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
// Use the specific simple server port for remove-background
const API_SERVER_URL = isDevelopment ? 'http://localhost:4002' : '';

// Maximum width for low-resolution images (for originals)
const LOW_RES_MAX_WIDTH = 600;
// JPEG quality for low-resolution images (0-1)
const LOW_RES_QUALITY = 0.7;

const MAX_REMOVE_BG_BYTES = 19 * 1024 * 1024; // ~19 MB safety margin below 20 MB limit

/**
 * Create a low-resolution version of an image
 * @param {File|Blob} imageFile - The original image file
 * @returns {Promise<Blob>} - Low-resolution image as Blob
 */
export const createLowResVersion = async (imageFile) => {
  console.log("Creating low-resolution version of image");
  return new Promise((resolve, reject) => {
    try {
      // Create an image element to load the file
      const img = new Image();
      img.onload = () => {
        // Create a canvas element for resizing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions while preserving aspect ratio
        let width = img.width;
        let height = img.height;
        
        // Only resize if the image is larger than our target width
        if (width > LOW_RES_MAX_WIDTH) {
          const ratio = height / width;
          width = LOW_RES_MAX_WIDTH;
          height = Math.round(width * ratio);
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw the image on the canvas at the new size
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with moderate JPEG compression
        canvas.toBlob((blob) => {
          console.log(`Low-res image created: ${Math.round(blob.size / 1024)}KB (${width}x${height})`);
          resolve(blob);
        }, 'image/jpeg', LOW_RES_QUALITY);
      };
      
      img.onerror = (error) => {
        console.error("Error loading image for low-res conversion:", error);
        reject(error);
      };
      
      // Load the image from the file
      img.src = URL.createObjectURL(imageFile);
    } catch (error) {
      console.error("Error creating low-resolution image:", error);
      reject(error);
    }
  });
};

/**
 * Compress image to JPEG (same dimensions) if size is above Remove.bg limit
 */
const compressIfTooLarge = async (file) => {
  if (file.size <= MAX_REMOVE_BG_BYTES) return file;
  console.log(`RemoveBG Service: File is ${(file.size/1024/1024).toFixed(1)} MB – compressing to stay under 20 MB.`);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        console.log('Compressed size:', (blob.size/1024/1024).toFixed(1), 'MB');
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.9); // high-quality JPEG
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Remove background from an image using direct API calls to Remove.bg
 * @param {File|Blob} imageFile - The image file or blob
 * @returns {Promise<Blob>} - Image with background removed as Blob
 */
export const removeBackground = async (imageFile) => {
  console.log("RemoveBG Service: Starting direct background removal");
  
  if (!imageFile) {
    console.error("RemoveBG Service: No image file provided");
    throw new Error('Image file is required');
  }

  console.log("RemoveBG Service: Preparing request with image type:", imageFile.type, "size:", Math.round(imageFile.size / 1024), "KB");
  const workingFile = await compressIfTooLarge(imageFile);
  if (workingFile !== imageFile) {
    console.log('RemoveBG Service: Using compressed file for API call.');
  }
  
  // Get the API key from environment variables
  const apiKey = import.meta.env.VITE_REMOVE_BG_API_KEY;
  
  if (!apiKey) {
    console.error("RemoveBG Service: API key not found");
    throw new Error('Remove.bg API key is missing (VITE_REMOVE_BG_API_KEY)');
  }
  
  // Create FormData for direct API call with trimming parameters
  const formData = new FormData();
  formData.append('image_file', workingFile);
  formData.append('size', 'auto');        // Use automatic size
  formData.append('format', 'png');       // PNG format for transparency
  formData.append('crop', 'true');        // Enable auto-cropping
  formData.append('trim', 'true');        // Enable trimming of excess transparent areas
  formData.append('scale', '100%');       // Don't resize
  formData.append('crop_margin', '10px'); // 10px margin around the subject (similar to our padding)
  
  try {
    console.log("RemoveBG Service: Sending direct request to Remove.bg API");
    const response = await fetch(REMOVE_BG_API_URL, {
      method: 'POST',
      headers: {
        'X-Api-Key': apiKey
      },
      body: formData
    });

    console.log("RemoveBG Service: Received response, status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("RemoveBG API Error:", response.status, errorText);
      
      // Try server fallback if direct API fails
      console.log("RemoveBG Service: Direct API failed, trying server fallback");
      return await removeBackgroundServerFallback(imageFile);
    }

    // Get the image as a blob
    const imageBlob = await response.blob();
    console.log("RemoveBG Service: Received image blob, type:", imageBlob.type, "size:", Math.round(imageBlob.size / 1024), "KB");
    
    return imageBlob;
  } catch (error) {
    console.error("RemoveBG Service: Error during direct API call:", error);
    
    // Try server fallback if direct API fails
    console.log("RemoveBG Service: Error in direct API, trying server fallback");
    return await removeBackgroundServerFallback(imageFile);
  }
};

/**
 * Fallback method that uses server for background removal
 * @param {File|Blob} imageFile - The image file or blob
 * @returns {Promise<Blob>} - Image with background removed as Blob
 */
export const removeBackgroundServerFallback = async (imageFile) => {
  console.log("RemoveBG Service: Starting background removal via server (fallback)");
  
  // Create FormData for our server API
  const formData = new FormData();
  formData.append('image', imageFile, 'image.jpg');

  // Direct URL to the Express server running on port 4000
  const serverUrl = `${API_SERVER_URL}/api/remove-background`;

  try {
    console.log("RemoveBG Service: Sending request to server endpoint:", serverUrl);
    const response = await fetch(serverUrl, {
      method: 'POST',
      body: formData
    });

    console.log("RemoveBG Service: Received response, status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("RemoveBG Server Error:", response.status, errorText);
      throw new Error('Background removal failed: ' + errorText);
    }

    // Get the image as a blob
    const imageBlob = await response.blob();
    console.log("RemoveBG Service: Received image blob, type:", imageBlob.type, "size:", Math.round(imageBlob.size / 1024), "KB");
    
    // For server fallback, we still need to crop the image as the server might not trim
    const croppedBlob = await autoCropTransparentImage(imageBlob);
    
    return croppedBlob;
  } catch (error) {
    console.error("RemoveBG Service: Error during server call:", error);
    throw error;
  }
};

/**
 * Auto-crop a transparent PNG image to tightly fit around the subject
 * @param {Blob} imageBlob - The transparent PNG image blob
 * @returns {Promise<Blob>} - Cropped image blob
 */
export const autoCropTransparentImage = (imageBlob) => {
  console.log("RemoveBG Service: Auto-cropping transparent image");
  return new Promise((resolve, reject) => {
    // Create an image to load the blob
    const img = new Image();
    img.onload = () => {
      try {
        // Create a canvas to process the image
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);
        
        // Get image data to analyze transparency
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Initialize bounds to extreme values
        let minX = img.width;
        let minY = img.height;
        let maxX = 0;
        let maxY = 0;
        let foundVisiblePixel = false;
        
        // Verbose logging for debugging
        console.log("RemoveBG Service: Image dimensions for cropping:", img.width, "x", img.height);
        
        // First pass - horizontal scan (left to right, for each row)
        for (let y = 0; y < img.height; y++) {
          for (let x = 0; x < img.width; x++) {
            const idx = (y * img.width + x) * 4;
            const alpha = data[idx + 3];
            
            // If pixel is not transparent (alpha > threshold)
            if (alpha > 20) {  // Higher threshold for more definitive edges
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
              foundVisiblePixel = true;
              // Once we find the leftmost non-transparent pixel in this row, we can move to next row
              break;
            }
          }
        }
        
        // Second pass - horizontal scan (right to left, for each row)
        for (let y = 0; y < img.height; y++) {
          for (let x = img.width - 1; x >= 0; x--) {
            const idx = (y * img.width + x) * 4;
            const alpha = data[idx + 3];
            
            if (alpha > 20) {
              maxX = Math.max(maxX, x);
              // Once we find the rightmost non-transparent pixel in this row, we can move to next row
              break;
            }
          }
        }
        
        // Third pass - vertical scan (top to bottom, for each column)
        for (let x = 0; x < img.width; x++) {
          for (let y = 0; y < img.height; y++) {
            const idx = (y * img.width + x) * 4;
            const alpha = data[idx + 3];
            
            if (alpha > 20) {
              minY = Math.min(minY, y);
              // Once we find the topmost non-transparent pixel in this column, we can move to next column
              break;
            }
          }
        }
        
        // Fourth pass - vertical scan (bottom to top, for each column)
        for (let x = 0; x < img.width; x++) {
          for (let y = img.height - 1; y >= 0; y--) {
            const idx = (y * img.width + x) * 4;
            const alpha = data[idx + 3];
            
            if (alpha > 20) {
              maxY = Math.max(maxY, y);
              // Once we find the bottommost non-transparent pixel in this column, we can move to next column
              break;
            }
          }
        }
        
        // Check if we found any visible content
        if (!foundVisiblePixel || minX >= maxX || minY >= maxY) {
          console.log("RemoveBG Service: No visible content found, returning original image");
          resolve(imageBlob);
          return;
        }
        
        // Add padding (10 pixels) around the subject
        const padding = 10;
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = Math.min(img.width - 1, maxX + padding);
        maxY = Math.min(img.height - 1, maxY + padding);
        
        // Calculate the new dimensions
        const width = maxX - minX + 1;
        const height = maxY - minY + 1;
        
        console.log(`RemoveBG Service: Cropping bounds - x:${minX}, y:${minY}, w:${width}, h:${height}`);
        
        // Create a new canvas with the cropped dimensions
        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = width;
        croppedCanvas.height = height;
        const croppedCtx = croppedCanvas.getContext('2d');
        
        // Draw the cropped portion onto the new canvas
        croppedCtx.drawImage(
          img,
          minX, minY, width, height,  // Source rectangle
          0, 0, width, height         // Destination rectangle
        );
        
        // Convert the cropped canvas to a blob with maximum quality
        croppedCanvas.toBlob((blob) => {
          console.log("RemoveBG Service: Cropped image created, type:", blob.type, "size:", Math.round(blob.size / 1024), "KB");
          resolve(blob);
        }, 'image/png', 1.0);  // Use highest quality for PNG
      } catch (error) {
        console.error("RemoveBG Service: Error during image cropping:", error);
        // Fall back to the original image on error
        resolve(imageBlob);
      }
    };
    
    img.onerror = (error) => {
      console.error("RemoveBG Service: Error loading image for cropping:", error);
      reject(error);
    };
    
    // Load the image from the blob
    img.src = URL.createObjectURL(imageBlob);
  });
};

/**
 * Convert a data URL to a file
 * @param {string} dataUrl - The data URL
 * @param {string} filename - The filename to use
 * @returns {File} - The file object
 */
export const dataURLtoFile = (dataUrl, filename) => {
  console.log("RemoveBG Service: Converting data URL to file");
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  const file = new File([u8arr], filename, { type: mime });
  console.log("RemoveBG Service: File created:", filename, "type:", mime, "size:", Math.round(file.size / 1024), "KB");
  return file;
};

/**
 * Convert a blob to a data URL
 * @param {Blob} blob - The image blob
 * @returns {Promise<string>} - Data URL
 */
export const blobToDataURL = (blob) => {
  console.log("RemoveBG Service: Converting blob to data URL");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log("RemoveBG Service: Data URL created, length:", reader.result.length);
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      console.error("RemoveBG Service: Error converting blob to data URL:", error);
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Process an image with remove.bg API and upload to Supabase storage
 * @param {File} imageFile - The image file to process and upload
 * @param {string} userId - The user's ID
 * @param {string} eventId - The event ID
 * @param {Array} tags - Optional tags for the image
 * @returns {Promise<Object>} - The uploaded image record
 */
export const processAndUploadImage = async (imageFile, userId, eventId, tags = null) => {
  try {
    // Check if user is authenticated
    const { data, error: authError } = await supabase.auth.getSession();
    
    if (authError) {
      console.error('Authentication error:', authError);
      throw new Error('Authentication error: ' + authError.message);
    }
    
    if (!data || !data.session) {
      console.error('No active session');
      throw new Error('No active session - please log in');
    }
    
    const session = data.session;
    
    console.log('Authenticated as:', session.user.email);
    console.log('Auth user ID:', session.user.id);
    console.log('Target user ID for upload:', userId);
    
    // Check if the authenticated user is admin/staff
    const { data: roleData, error: roleError } = await supabase
      .from('app_users')
      .select('user_type')
      .eq('id', session.user.id)
      .single();
      
    if (roleError) {
      console.error('Error checking user role:', roleError);
    } else {
      console.log('User role:', roleData?.user_type || 'unknown');
    }
    
    // Step 1: Create and upload low-resolution version of the original image
    console.log('Creating low-resolution version of original image...');
    const lowResBlob = await createLowResVersion(imageFile);
    
    // Generate a unique filename for the low-res original
    const lowResFileName = userId + '_' + Date.now() + '_' + Math.floor(Math.random() * 10000) + '_original_lowres.jpg';
    // Use specific folder structure with an originals subfolder
    const lowResFilePath = 'events/' + eventId + '/users/' + userId + '/originals/' + lowResFileName;
    
    // Create a file from the low-res blob
    const lowResFile = new File(
      [lowResBlob],
      lowResFileName,
      { type: 'image/jpeg' }
    );
    
    // Upload the low-resolution original to Supabase storage
    console.log('Uploading low-resolution original to Supabase storage...');
    const { data: lowResUploadData, error: lowResUploadError } = await supabase.storage
      .from('playmakerpics-images')
      .upload(lowResFilePath, lowResFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'image/jpeg'
      });
      
    if (lowResUploadError) {
      console.error('Error uploading low-res original:', lowResUploadError);
      // Continue with the process even if low-res upload fails
    }
    
    // Get public URL for the low-res original
    let originalImageUrl = null;
    if (!lowResUploadError) {
      const { data: { publicUrl: lowResPublicUrl } } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(lowResFilePath);
      originalImageUrl = lowResPublicUrl;
      console.log('Low-res original image URL:', originalImageUrl);
    }
    
    // Step 2: Process with Remove.bg using direct API calls with built-in trimming
    console.log('Processing with Remove.bg API (direct client-side)...');
    
    // Log details about the image being processed
    console.log('Processing image:', imageFile.name);
    console.log('Image size:', Math.round(imageFile.size / 1024), 'KB');
    console.log('Image type:', imageFile.type);

    // Call our enhanced removeBackground function
    // This already includes built-in trimming and will try server fallback if needed
    console.log('Calling Remove.bg API with trimming capability...');
    const processedBlob = await removeBackground(imageFile);
    console.log('Processed image size:', Math.round(processedBlob.size / 1024), 'KB');
    
    // Auto-cropping is no longer needed as it's handled by Remove.bg API's trim parameter
    // The processedBlob is already trimmed appropriately
    
    // Create a file from the processed blob
    const processedFile = new File(
      [processedBlob],
      imageFile.name.split('.')[0] + '-bg-removed-hq.png',
      { type: 'image/png' }
    );
    
    // Generate a unique filename
    const fileName = userId + '_' + Date.now() + '_' + Math.floor(Math.random() * 10000) + '_hq.png';
    // Use specific folder structure
    const filePath = 'events/' + eventId + '/users/' + userId + '/' + fileName;
    
    // Upload the background-removed image to Supabase storage
    console.log('Uploading to Supabase storage with high quality settings...');
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('playmakerpics-images')
      .upload(filePath, processedFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'image/png'
      });
      
    if (uploadError) {
      throw new Error('Storage upload failed: ' + uploadError.message);
    }
    
    // Get public URL for the processed image
    const { data: { publicUrl } } = supabase.storage
      .from('playmakerpics-images')
      .getPublicUrl(filePath);
    
    // Create watermarked version of the image
    console.log('Creating watermarked version of the image...');
    const watermarkedBlob = await addWatermark(processedBlob, {
      text: "PlayMaker Pics",
      color: "rgba(255, 255, 255, 0.8)",
      font: "bold 36px Arial",
      repeating: true,
      diagonal: true,
    });
    
    // Create a file for the watermarked version
    const watermarkedFile = new File(
      [watermarkedBlob],
      imageFile.name.split('.')[0] + '-bg-removed-hq-watermarked.png',
      { type: 'image/png' }
    );
    
    // Generate a filename for the watermarked version
    const watermarkedFileName = userId + '_' + Date.now() + '_' + Math.floor(Math.random() * 10000) + '_hq_wm.png';
    // Use specific folder structure with a watermarked subfolder
    const watermarkedFilePath = 'events/' + eventId + '/users/' + userId + '/watermarked/' + watermarkedFileName;
    
    // Upload the watermarked image to Supabase storage
    console.log('Uploading watermarked image to Supabase storage...');
    const { data: watermarkedUploadData, error: watermarkedUploadError } = await supabase.storage
      .from('playmakerpics-images')
      .upload(watermarkedFilePath, watermarkedFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'image/png'
      });
      
    if (watermarkedUploadError) {
      console.error('Watermarked image upload failed:', watermarkedUploadError);
      // Continue with the process even if watermarked upload fails
    }
    
    // Get public URL for the watermarked image
    let watermarkedPublicUrl = null;
    if (!watermarkedUploadError) {
      const { data: { publicUrl: wmPublicUrl } } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(watermarkedFilePath);
      
      watermarkedPublicUrl = wmPublicUrl;
      console.log('Watermarked image URL:', watermarkedPublicUrl);
    }
    
    // First try direct insert as the authenticated user should have permission
    console.log('Attempting direct insert with authenticated session...');
    
    // Test RLS policy with a query first
    try {
      const { count, error: countError } = await supabase
        .from('user_uploads')
        .select('id', { count: 'exact', head: true });
      
      if (countError) {
        console.error('RLS test query failed:', countError);
      } else {
        console.log('RLS test passed! Can query user_uploads table');
      }
    } catch (e) {
      console.error('Error testing RLS policy:', e);
    }
    
    try {
      const { data: dbData, error: dbError } = await supabase
        .from('user_uploads')
        .insert({
          user_id: userId,
          file_url: publicUrl,
          file_url_wm: watermarkedPublicUrl, // Add the watermarked URL
          source_file_url: originalImageUrl, // Changed from original_image_url to source_file_url
          is_primary: false,
          upload_type: 'event_upload:' + eventId,
          file_variant: 'processed',
          status: 'active',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
        
      if (dbError) {
        console.error('Direct insert error:', dbError);
        
        // Try a simplified insert as fallback (without using RPC)
        console.log('Attempting direct insert without RPC as fallback...');
        try {
          const { data: simpleInsertData, error: simpleInsertError } = await supabase
            .from('user_uploads')
            .insert({
              user_id: userId,
              file_url: publicUrl,
              file_url_wm: watermarkedPublicUrl,
              source_file_url: originalImageUrl,
              is_primary: false,
              upload_type: 'event_upload:' + eventId,
              file_variant: 'processed',
              status: 'active',
              created_at: new Date().toISOString()
            })
            .select()
            .single();
            
          if (simpleInsertError) {
            console.error('Simple insert also failed:', simpleInsertError);
            throw simpleInsertError;
          }
          
          console.log('Simple insert successful:', simpleInsertData);
          
          return {
            id: simpleInsertData.id,
            user_id: userId,
            event_id: eventId,
            image_url: publicUrl,
            file_url: publicUrl,
            file_url_wm: watermarkedPublicUrl,
            source_file_url: originalImageUrl,
            file_path: filePath,
            status: 'active',
            file_variant: 'processed',
            tags: tags,
            created_at: simpleInsertData.created_at,
            is_primary: simpleInsertData.is_primary,
            upload_type: simpleInsertData.upload_type
          };
        } catch (fallbackError) {
          console.error('All insert attempts failed:', fallbackError);
          // Continue to local mode
        }
        
        // If all database operations fail, create a local record
        console.log('All database operations failed, creating local image record...');
        return {
          id: 'local-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15),
          user_id: userId,
          event_id: eventId,
          image_url: publicUrl,
          file_url: publicUrl,
          file_url_wm: watermarkedPublicUrl,
          source_file_url: originalImageUrl,
          file_path: filePath,
          status: 'local',
          file_variant: 'processed',
          upload_type: 'event_upload:' + eventId,
          tags: tags,
          created_at: new Date().toISOString()
        };
      }
      
      console.log('Direct insert successful:', dbData);
      
      // Return in the format expected by the component
      return {
        id: dbData.id,
        user_id: userId,
        event_id: eventId, // Add this for component compatibility
        image_url: publicUrl,
        file_url: publicUrl,
        file_url_wm: watermarkedPublicUrl, // Add the watermarked URL
        source_file_url: originalImageUrl, // Changed from original_image_url to source_file_url
        file_path: filePath,
        status: 'active',
        file_variant: 'processed',
        tags: tags, // Keep tags in memory only
        created_at: dbData.created_at,
        is_primary: dbData.is_primary,
        upload_type: dbData.upload_type
      };
    } catch (dbError) {
      console.warn('Database insert failed, using local mode instead:', dbError);
      
      // Create a mock record with the real image URL from storage
      return {
        id: 'local-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15),
        user_id: userId,
        event_id: eventId,
        image_url: publicUrl, // Use the real URL from storage
        file_url: publicUrl,
        file_url_wm: watermarkedPublicUrl, // Add the watermarked URL
        source_file_url: originalImageUrl, // Add the low-res original URL
        file_path: filePath,
        status: 'local',
        file_variant: 'processed',
        upload_type: 'event_upload:' + eventId,
        tags: tags,
        created_at: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error("Error processing and uploading image:", error);
    throw error;
  }
};

/**
 * Fallback to local processing if remove.bg fails
 * @param {File} imageFile - The image file to process
 * @param {string} userId - The user's ID
 * @param {string} eventId - The event ID
 * @param {Array} tags - Optional tags for the image
 * @returns {Promise<Object>} - Mock image data for demo mode
 */
export const createLocalImageRecord = async (imageFile, userId, eventId, tags = null) => {
  // Create a local URL from the original file
  const imageUrl = URL.createObjectURL(imageFile);
  
  // Create a mock image record
  return {
    id: 'local-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15),
    user_id: userId,
    event_id: eventId,
    image_url: imageUrl,
    file_url: imageUrl,
    source_file_url: imageUrl, // For local mode, use the same URL for original
    status: 'local',
    file_variant: 'processed',
    upload_type: 'event_upload:' + eventId,
    tags: tags,
    created_at: new Date().toISOString()
  };
};